import { skipToken } from '@reduxjs/toolkit/query';
import { compact, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { type SelectableValue } from '@grafana/data';
import { t } from '@grafana/i18n';
import { type ComboboxOption } from '@grafana/ui';
import { useGetDisplayMappingQuery } from 'app/api/clients/iam/v0alpha1';
import { type TermCount } from 'app/core/components/TagFilter/TagFilter';
import { NewDashboardLibraryInteractions } from 'app/features/dashboard/dashgrid/DashboardLibrary/analytics/main';
import {
  CONTENT_KINDS,
  EVENT_LOCATIONS,
  type SourceEntryPoint,
} from 'app/features/dashboard/dashgrid/DashboardLibrary/constants';

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

interface UseTemplateFilteringOptions<T> {
  items: T[];
  getTitle: (item: T) => string;
  getDescription: (item: T) => string;
  getTags?: (item: T) => string[];
  getCreatorUid?: (item: T) => string;
  entryPoint: SourceEntryPoint;
}

export function useTemplateFiltering<T>({
  items,
  getTitle,
  getDescription,
  getTags,
  getCreatorUid,
  entryPoint,
}: UseTemplateFilteringOptions<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCreators, setSelectedCreators] = useState<string[]>([]);
  const [sortValue, setSortValue] = useState<string | undefined>(undefined);

  // Debounce search-performed analytics so we don't fire on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(() => setDebouncedSearch(searchQuery), 500, [searchQuery]);
  const lastFiredSearch = useRef('');

  // Extract unique creator UIDs for display name resolution
  const creatorUids = useMemo(() => {
    if (!getCreatorUid) {
      return undefined;
    }
    return uniq(compact(items.map(getCreatorUid)));
  }, [items, getCreatorUid]);

  // Resolve UIDs to display names via IAM API (same pattern as Saved Queries)
  const { data: displayMapping } = useGetDisplayMappingQuery(
    creatorUids && creatorUids.length > 0 ? { key: creatorUids } : skipToken
  );

  // Build a UID → displayName map
  const creatorDisplayMap = useMemo(() => {
    const map = new Map<string, string>();
    if (displayMapping?.display) {
      for (const entry of displayMapping.display) {
        const uid = `${entry.identity.type}:${entry.identity.name}`;
        map.set(uid, entry.displayName);
      }
    }
    return map;
  }, [displayMapping]);

  // Tag options as TermCount[] for TagFilter component
  const tagTermCounts = useMemo<TermCount[]>(() => {
    if (!getTags) {
      return [];
    }
    const tagCounts = new Map<string, number>();
    for (const item of items) {
      for (const tag of getTags(item)) {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      }
    }
    return Array.from(tagCounts.entries())
      .sort(([a], [b]) => collator.compare(a, b))
      .map(([term, count]) => ({ term, count }));
  }, [items, getTags]);

  // Async callback for TagFilter's tagOptions prop
  const getTagOptions = useCallback((): Promise<TermCount[]> => {
    return Promise.resolve(tagTermCounts);
  }, [tagTermCounts]);

  // Unique creator options with display names
  const creatorOptions = useMemo<Array<ComboboxOption<string>>>(() => {
    if (!creatorUids) {
      return [];
    }
    return creatorUids.map((uid) => ({
      value: uid,
      label: creatorDisplayMap.get(uid) || uid,
    }));
  }, [creatorUids, creatorDisplayMap]);

  // Sort options for SortPicker
  const getSortOptions = () => {
    return Promise.resolve([alphabeticallySortingOption(), alphabeticallyReverseSortingOption()]);
  };

  const alphabeticallySortingOption = () => ({
    value: 'asc',
    label: t('dashboard-library.custom-dashboard-template-filters.sort.asc', 'Alphabetically (A–Z)'),
    sort: (a: T, b: T) => collator.compare(getTitle(a), getTitle(b)),
  });

  const alphabeticallyReverseSortingOption = () => ({
    value: 'desc',
    label: t('dashboard-library.custom-dashboard-template-filters.sort.desc', 'Alphabetically (Z–A)'),
    sort: (a: T, b: T) => collator.compare(getTitle(b), getTitle(a)),
  });

  // Filter and sort
  const filteredItems = useMemo(() => {
    let result = items;

    // Search filter (title + description)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const title = getTitle(item).toLowerCase();
        const description = getDescription(item).toLowerCase();
        return title.includes(query) || description.includes(query);
      });
    }

    // Tag filter (OR: template must have at least one selected tag)
    if (selectedTags.length > 0 && getTags) {
      result = result.filter((item) => {
        const itemTags = getTags(item);
        return selectedTags.some((tag) => itemTags.includes(tag));
      });
    }

    // Creator filter (OR: template creator must be one of selected)
    if (selectedCreators.length > 0 && getCreatorUid) {
      result = result.filter((item) => {
        const uid = getCreatorUid(item);
        return selectedCreators.includes(uid);
      });
    }

    // Sort
    if (sortValue) {
      result = [...result].sort((a, b) => {
        const titleA = getTitle(a);
        const titleB = getTitle(b);
        return sortValue === 'asc' ? collator.compare(titleA, titleB) : collator.compare(titleB, titleA);
      });
    }

    return result;
  }, [items, searchQuery, selectedTags, selectedCreators, sortValue, getTitle, getDescription, getTags, getCreatorUid]);

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (!trimmed || trimmed === lastFiredSearch.current) {
      return;
    }
    lastFiredSearch.current = trimmed;
    NewDashboardLibraryInteractions.searchPerformed({
      contentKinds: [CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE],
      datasourceTypes: [],
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.DASHBOARD_TEMPLATES_MODAL,
      hasResults: filteredItems.length > 0,
      resultCount: filteredItems.length,
    });
  }, [debouncedSearch, filteredItems.length, entryPoint]);

  const onTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    NewDashboardLibraryInteractions.filtersApplied({
      contentKinds: [CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE],
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.DASHBOARD_TEMPLATES_MODAL,
      filterType: 'tags',
      selectedCount: tags.length,
      resultCount: filteredItems.length,
    });
  };

  const onCreatorsChange = (creators: Array<ComboboxOption<string>>) => {
    setSelectedCreators(creators.map((c) => c.value));
    NewDashboardLibraryInteractions.filtersApplied({
      contentKinds: [CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE],
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.DASHBOARD_TEMPLATES_MODAL,
      filterType: 'creators',
      selectedCount: creators.length,
      resultCount: filteredItems.length,
    });
  };

  const onSortChange = (change: SelectableValue) => {
    setSortValue(change?.value);
    NewDashboardLibraryInteractions.filtersApplied({
      contentKinds: [CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE],
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.DASHBOARD_TEMPLATES_MODAL,
      filterType: 'sort',
      selectedCount: change?.value ? 1 : 0,
      sortValue: change?.value,
      resultCount: filteredItems.length,
    });
  };

  return {
    searchQuery,
    setSearchQuery,
    selectedTags,
    onTagsChange,
    selectedCreators,
    onCreatorsChange,
    sortValue,
    onSortChange,
    filteredItems,
    getTagOptions,
    creatorOptions,
    getSortOptions,
  };
}
